import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../common/Modal';
import { MerchandiseItem, UserProfile } from '../../types';
import { useToast } from '../common/Toast';
import { uploadProductImageToDrive } from '../../lib/gdrive';
import { saveDriveFileDB } from '../../services/db';
import { getUniformPriceBySize } from '../../utils/pricing';
import {
  Tag,
  Upload,
  Image as ImageIcon,
  X,
  Plus,
  Sparkles,
  Info,
  DollarSign,
  Layers,
  CheckCircle2,
  Package
} from 'lucide-react';

interface ProductEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: MerchandiseItem | null;
  onSave: (item: MerchandiseItem) => Promise<void>;
  currentUser?: UserProfile | null;
}

export const ProductEditModal: React.FC<ProductEditModalProps> = ({
  isOpen,
  onClose,
  product,
  onSave,
  currentUser
}) => {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState<'Pakaian' | 'Kelengkapan' | 'Aksesori'>('Pakaian');
  const [formBasePrice, setFormBasePrice] = useState<number>(0);
  const [hasSizePricing, setHasSizePricing] = useState(false);
  const [formSizes, setFormSizes] = useState<string>('');
  const [formSizePrices, setFormSizePrices] = useState<Record<string, number>>({});
  const [bulkPriceInput, setBulkPriceInput] = useState<string>('');
  const [formStockStatus, setFormStockStatus] = useState<'Dalam Stok' | 'Stok Rendah' | 'Tiada Stok'>('Dalam Stok');
  const [formStockQuantity, setFormStockQuantity] = useState<number>(0);
  const [formVariations, setFormVariations] = useState<string>('');
  const [formDeliveryMethods, setFormDeliveryMethods] = useState<('Ambil di Gelanggang' | 'Penghantaran Pos')[]>(['Ambil di Gelanggang']);
  const [formDescription, setFormDescription] = useState('');
  const [formPhotoUrl, setFormPhotoUrl] = useState('');
  const [formSpecs, setFormSpecs] = useState<{ key: string; value: string }[]>([{ key: '', value: '' }]);

  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize or reset form when product or isOpen changes
  useEffect(() => {
    if (!isOpen) return;

    if (product) {
      setFormName(product.name || '');
      setFormCategory(product.category || 'Pakaian');
      setFormBasePrice(product.price || 0);

      const sizes = product.sizes ? [...product.sizes] : [];
      setFormSizes(sizes.join(', '));

      const isBaju = product.id === 'merch-baju-silat' || (product.name && product.name.toLowerCase().includes('baju silat'));
      const existingSizePrices = product.sizePrices ? { ...product.sizePrices } : {};

      // If Baju Silat and no sizePrices were saved, seed with official pricing
      if (isBaju && Object.keys(existingSizePrices).length === 0) {
        sizes.forEach(s => {
          existingSizePrices[s] = getUniformPriceBySize(s);
        });
      }

      setFormSizePrices(existingSizePrices);
      setHasSizePricing(Boolean(Object.keys(existingSizePrices).length > 0 || isBaju));

      setFormStockStatus(product.stockStatus || 'Dalam Stok');
      setFormStockQuantity(product.stockQuantity || 0);
      setFormVariations(product.variations ? product.variations.join(', ') : '');
      const existingMethods = product.deliveryMethods && product.deliveryMethods.length > 0
        ? [...product.deliveryMethods]
        : (['Ambil di Gelanggang'] as ('Ambil di Gelanggang' | 'Penghantaran Pos')[]);
      setFormDeliveryMethods(existingMethods);
      setFormDescription(product.description || '');
      setFormPhotoUrl(product.photoUrl || '');

      const specsList = product.specifications
        ? Object.entries(product.specifications).map(([k, v]) => ({ key: k, value: v }))
        : [{ key: '', value: '' }];
      setFormSpecs(specsList.length > 0 ? specsList : [{ key: '', value: '' }]);
    } else {
      setFormName('');
      setFormCategory('Pakaian');
      setFormBasePrice(0);
      setHasSizePricing(false);
      setFormSizes('');
      setFormSizePrices({});
      setFormStockStatus('Dalam Stok');
      setFormStockQuantity(50);
      setFormVariations('');
      setFormDeliveryMethods(['Ambil di Gelanggang']);
      setFormDescription('');
      setFormPhotoUrl('/logo-pssasmm.png');
      setFormSpecs([{ key: '', value: '' }]);
    }
  }, [product, isOpen]);

  // Parse current sizes array from comma separated string
  const parsedSizes = React.useMemo(() => {
    return formSizes
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }, [formSizes]);

  // Sync size price keys when parsedSizes changes
  useEffect(() => {
    if (!hasSizePricing) return;

    setFormSizePrices(prev => {
      const next = { ...prev };
      parsedSizes.forEach(s => {
        if (next[s] === undefined || next[s] === null) {
          // If Baju Silat, auto assign standard tier price
          if (formName.toLowerCase().includes('baju silat') || product?.id === 'merch-baju-silat') {
            next[s] = getUniformPriceBySize(s);
          } else {
            next[s] = formBasePrice > 0 ? formBasePrice : 0;
          }
        }
      });
      return next;
    });
  }, [parsedSizes, hasSizePricing, formName, product?.id, formBasePrice]);

  // Apply Uniform Silat presets
  const applyUniformPresets = () => {
    const uniformSizes = ['110cm', '120cm', '130cm', '140cm', '150cm', '160cm', '170cm', '180cm', '190cm', '200cm', '210cm'];
    setFormSizes(uniformSizes.join(', '));
    setHasSizePricing(true);

    const prices: Record<string, number> = {};
    uniformSizes.forEach(s => {
      prices[s] = getUniformPriceBySize(s);
    });
    setFormSizePrices(prices);
    if (!formBasePrice || formBasePrice === 0) {
      setFormBasePrice(120);
    }
    showToast('info', 'Preset Baju Silat Diterapkan', 'Saiz 110cm–210cm & harga RM120, RM130, RM140 telah diisi.');
  };

  // Apply Standard Clothing presets (XS - 3XL)
  const applyStandardClothingPresets = () => {
    const standardSizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
    setFormSizes(standardSizes.join(', '));
    setHasSizePricing(true);

    const prices: Record<string, number> = {};
    standardSizes.forEach(s => {
      prices[s] = formBasePrice > 0 ? formBasePrice : 35;
    });
    setFormSizePrices(prices);
    showToast('info', 'Preset Saiz Baju Diterapkan', 'Saiz XS hingga 3XL telah dimasukkan.');
  };

  // Apply Bengkung color presets
  const applyBengkungPresets = () => {
    const bengkungColors = ['Hitam', 'Putih', 'Merah', 'Kuning', 'Biru'];
    setFormSizes(bengkungColors.join(', '));
    setFormVariations('Kosong, Cula 1, Cula 2, Cula 3, Cula 4');
    setHasSizePricing(false);
    showToast('info', 'Preset Bengkung Diterapkan', 'Warna dan variasi cula rasmi telah dimasukkan.');
  };

  // Bulk set all sizes to custom price
  const handleApplyBulkPrice = () => {
    const amount = parseFloat(bulkPriceInput);
    if (isNaN(amount) || amount <= 0) {
      showToast('error', 'Nilai Tidak Sah', 'Sila masukkan nombor harga yang sah.');
      return;
    }

    const updated: Record<string, number> = {};
    parsedSizes.forEach(s => {
      updated[s] = amount;
    });
    setFormSizePrices(updated);
    setBulkPriceInput('');
    showToast('success', 'Harga Saiz Diseragamkan', `Semua saiz kini ditetapkan pada RM ${amount.toFixed(2)}.`);
  };

  // Update single size price
  const handleUpdateSizePrice = (size: string, val: string) => {
    const num = parseFloat(val);
    setFormSizePrices(prev => ({
      ...prev,
      [size]: isNaN(num) ? 0 : num
    }));
  };

  // Upload image to Cloudinary
  const handleProductImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('error', 'Fail Tidak Sah', 'Sila pilih fail imej (PNG, JPG, JPEG, WEBP).');
      return;
    }

    setIsUploadingPhoto(true);
    showToast('info', 'Memuat Naik Gambar', 'Menyimpan fail ke Cloudinary CDN...');

    try {
      const res = await uploadProductImageToDrive(file, product?.id, formName || 'produk');
      setFormPhotoUrl(res.dataUrl);
      await saveDriveFileDB(res.driveItem).catch(() => { });

      showToast('success', 'Gambar Berjaya Dimuat Naik', 'Imej produk telah disimpan ke Cloudinary CDN.');
    } catch (err: any) {
      console.error('Cloudinary upload error:', err);
      showToast('error', 'Ralat Muat Naik', 'Gagal memuat naik gambar produk.');
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Spec fields helpers
  const addSpecField = () => setFormSpecs(prev => [...prev, { key: '', value: '' }]);
  const removeSpecField = (idx: number) => setFormSpecs(prev => prev.filter((_, i) => i !== idx));
  const updateSpecField = (idx: number, field: 'key' | 'value', val: string) => {
    setFormSpecs(prev => {
      const next = [...prev];
      next[idx][field] = val;
      return next;
    });
  };

  // Form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formName.trim()) {
      showToast('error', 'Diperlukan', 'Sila masukkan nama produk.');
      return;
    }

    const sizesArr = parsedSizes;
    const variationsArr = formVariations.split(',').map(v => v.trim()).filter(Boolean);

    // Build clean specs
    const specsObj: Record<string, string> = {};
    formSpecs.forEach(s => {
      if (s.key.trim() && s.value.trim()) {
        specsObj[s.key.trim()] = s.value.trim();
      }
    });

    // Clean size prices: only keep sizes that currently exist in parsedSizes
    let cleanSizePrices: Record<string, number> | undefined = undefined;
    if (hasSizePricing && sizesArr.length > 0) {
      cleanSizePrices = {};
      sizesArr.forEach(s => {
        const p = formSizePrices[s];
        cleanSizePrices![s] = typeof p === 'number' && p > 0 ? p : (formBasePrice > 0 ? formBasePrice : 0);
      });
    }

    // Determine baseline price (min price if sizePricing enabled, else formBasePrice)
    let finalBasePrice = Number(formBasePrice) || 0;
    if (cleanSizePrices && Object.keys(cleanSizePrices).length > 0) {
      const validPrices = Object.values(cleanSizePrices).filter(p => p > 0);
      if (validPrices.length > 0) {
        finalBasePrice = Math.min(...validPrices);
      }
    }

    const payload: MerchandiseItem = {
      id: product ? product.id : `merch-${Date.now()}`,
      name: formName.trim(),
      category: formCategory,
      price: finalBasePrice,
      description: formDescription.trim(),
      photoUrl: formPhotoUrl.trim() || '/logo-pssasmm.png',
      sizes: sizesArr.length > 0 ? sizesArr : undefined,
      sizePrices: cleanSizePrices,
      stockQuantity: typeof formStockQuantity === 'number' ? formStockQuantity : 0,
      stockStatus: formStockStatus,
      variations: variationsArr.length > 0 ? variationsArr : undefined,
      deliveryMethods: formDeliveryMethods.length > 0 ? formDeliveryMethods : ['Ambil di Gelanggang'],
      specifications: Object.keys(specsObj).length > 0 ? specsObj : undefined,
      userId: product?.userId || currentUser?.id || 'ASMM-011'
    };

    setIsSaving(true);
    try {
      await onSave(payload);
      showToast(
        'success',
        product ? 'Produk Dikemaskini' : 'Produk Didaftarkan',
        `Butiran barangan "${payload.name}" berjaya disimpan.`
      );
      onClose();
    } catch (err: any) {
      console.error('Error saving product:', err);
      showToast('error', 'Ralat Menyimpan', 'Gagal menyimpan butiran produk.');
    } finally {
      setIsSaving(false);
    }
  };

  // Price range summary calculation
  const priceRangeSummary = React.useMemo(() => {
    if (!hasSizePricing || parsedSizes.length === 0) return null;
    const values = parsedSizes
      .map(s => formSizePrices[s])
      .filter(p => typeof p === 'number' && !isNaN(p) && p > 0);

    if (values.length === 0) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    return {
      min,
      max,
      isUniform: min === max,
      label: min === max ? `RM ${min.toFixed(2)}` : `RM ${min.toFixed(2)} – RM ${max.toFixed(2)}`
    };
  }, [hasSizePricing, parsedSizes, formSizePrices]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? 'Kemaskini Butiran Produk' : 'Tambah Produk Baru'}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5 text-xs font-sans text-slate-800">
        {/* Top Product Banner / Header Info */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden shadow-xs">
              {formPhotoUrl ? (
                <img
                  src={formPhotoUrl}
                  alt="Pratonton"
                  className="w-full h-full object-cover"
                  onError={e => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                />
              ) : (
                <Package className="w-6 h-6 text-slate-400" />
              )}
            </div>
            <div>
              <h4 className="font-extrabold text-sm text-slate-900 leading-tight">
                {formName || 'Nama Produk Belum Dinyatakan'}
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-md text-asmm-red bg-red-50 border border-red-100 uppercase tracking-wider">
                  {formCategory}
                </span>
                <span className="text-slate-350">•</span>
                <span className="text-[11px] font-extrabold text-slate-700">
                  {priceRangeSummary ? priceRangeSummary.label : `RM ${(formBasePrice || 0).toFixed(2)}`}
                </span>
              </div>
            </div>
          </div>

          {product && (
            <span className="text-[10px] font-mono font-bold text-slate-400 bg-white px-2.5 py-1 rounded-lg border border-slate-200">
              ID: {product.id}
            </span>
          )}
        </div>

        {/* SECTION 1: NAMA, KATEGORI, HARGA ASAS */}
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3.5">
          <div className="sm:col-span-8">
            <label className="block text-slate-700 font-extrabold mb-1">
              Nama Produk <span className="text-asmm-red">*</span>
            </label>
            <input
              type="text"
              required
              value={formName}
              onChange={e => setFormName(e.target.value)}
              placeholder="Contoh: Baju Silat Awang Sulung Merah Muda"
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red shadow-xs"
            />
          </div>

          <div className="sm:col-span-4">
            <label className="block text-slate-700 font-extrabold mb-1">
              Kategori <span className="text-asmm-red">*</span>
            </label>
            <select
              value={formCategory}
              onChange={e => setFormCategory(e.target.value as any)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red shadow-xs"
            >
              <option value="Pakaian">Pakaian</option>
              <option value="Kelengkapan">Kelengkapan Silat</option>
              <option value="Aksesori">Aksesori</option>
            </select>
          </div>

          {/* Base Price */}
          <div className="sm:col-span-6">
            <label className="block text-slate-700 font-extrabold mb-1">
              {hasSizePricing ? 'Harga Asas / Minimum (RM)' : 'Harga Jualan (RM) *'}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-slate-400 font-extrabold">RM</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formBasePrice || ''}
                onChange={e => setFormBasePrice(parseFloat(e.target.value) || 0)}
                placeholder="Contoh: 120.00"
                className="w-full pl-10 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red shadow-xs"
              />
            </div>
            {hasSizePricing && (
              <p className="text-[10px] text-slate-400 mt-1">
                Akan auto diselaraskan mengikut harga saiz terendah.
              </p>
            )}
          </div>

          {/* Stock Quantity */}
          <div className="sm:col-span-3">
            <label className="block text-slate-700 font-extrabold mb-1">Kuantiti Stok *</label>
            <input
              type="number"
              min="0"
              required
              value={formStockQuantity}
              onChange={e => setFormStockQuantity(Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="50"
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red shadow-xs"
            />
          </div>

          {/* Stock Status */}
          <div className="sm:col-span-3">
            <label className="block text-slate-700 font-extrabold mb-1">Status Stok *</label>
            <select
              value={formStockStatus}
              onChange={e => setFormStockStatus(e.target.value as any)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red shadow-xs"
            >
              <option value="Dalam Stok">Dalam Stok</option>
              <option value="Stok Rendah">Stok Rendah</option>
              <option value="Tiada Stok">Tiada Stok</option>
            </select>
          </div>
        </div>

        {/* SECTION 2: PILIHAN SAIZ & PENETAPAN HARGA MENGIKUT SAIZ */}
        <div className="border border-slate-200 rounded-2xl p-4 bg-white shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div>
              <h4 className="font-extrabold text-slate-900 text-xs flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-asmm-red" />
                <span>Pilihan Saiz & Penetapan Harga Berdasarkan Saiz</span>
              </h4>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Masukkan pilihan saiz dan tetapkan harga berbeza untuk setiap saiz (cth: Baju Silat 110cm–210cm).
              </p>
            </div>

            {/* Quick Presets Buttons */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={applyUniformPresets}
                className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-asmm-red border border-red-200 rounded-lg font-extrabold text-[10px] transition-colors flex items-center gap-1 cursor-pointer"
                title="Guna tetapan rasmi uniform persatuan (110cm - 210cm)"
              >
                <Sparkles className="w-3 h-3 text-amber-500" />
                <span>Preset Uniform ASMM</span>
              </button>
              <button
                type="button"
                onClick={applyStandardClothingPresets}
                className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-lg font-bold text-[10px] transition-colors cursor-pointer"
              >
                Preset Baju (XS–3XL)
              </button>
              <button
                type="button"
                onClick={applyBengkungPresets}
                className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-lg font-bold text-[10px] transition-colors cursor-pointer"
              >
                Preset Bengkung
              </button>
            </div>
          </div>

          {/* Sizes Input field */}
          <div>
            <label className="block text-slate-700 font-extrabold mb-1">
              Senarai Pilihan Saiz / Warna (Asingkan dengan koma)
            </label>
            <input
              type="text"
              value={formSizes}
              onChange={e => setFormSizes(e.target.value)}
              placeholder="Contoh: 110cm, 120cm, 130cm, 140cm, 150cm, 160cm, 170cm, 180cm, 190cm, 200cm, 210cm"
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red shadow-xs font-mono"
            />
            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              {parsedSizes.map(s => (
                <span
                  key={s}
                  className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 rounded-md text-[10px] font-bold font-mono"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* Size Pricing Toggle */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="toggleSizePricing"
                checked={hasSizePricing}
                onChange={e => setHasSizePricing(e.target.checked)}
                className="w-4 h-4 text-asmm-red rounded border-slate-300 focus:ring-red-400 cursor-pointer accent-red-600"
              />
              <label htmlFor="toggleSizePricing" className="text-xs font-extrabold text-slate-800 cursor-pointer select-none">
                Aktifkan Harga Berbeza Mengikut Saiz
              </label>
            </div>

            {hasSizePricing && priceRangeSummary && (
              <span className="text-[11px] font-extrabold px-2.5 py-0.5 rounded-full bg-red-50 text-asmm-red border border-red-200">
                Julat Harga: {priceRangeSummary.label}
              </span>
            )}
          </div>

          {/* Interactive Size Price Grid */}
          {hasSizePricing && parsedSizes.length > 0 && (
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 space-y-3">
              {/* Bulk Apply Bar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 bg-white p-2.5 rounded-lg border border-slate-200">
                <span className="text-[11px] font-bold text-slate-600">
                  Kemaskini pantas semua harga saiz sekaligus:
                </span>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="relative">
                    <span className="absolute left-2.5 top-1.5 text-slate-400 font-bold text-[10px]">RM</span>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      value={bulkPriceInput}
                      onChange={e => setBulkPriceInput(e.target.value)}
                      placeholder="120.00"
                      className="w-24 pl-8 pr-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:outline-none focus:border-asmm-red"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyBulkPrice}
                    className="px-3 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-extrabold text-[11px] transition-colors cursor-pointer"
                  >
                    Set Semua Saiz
                  </button>
                </div>
              </div>

              {/* Grid of Sizes with individual price input */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                {parsedSizes.map(size => {
                  const currentPrice = formSizePrices[size] !== undefined ? formSizePrices[size] : formBasePrice;
                  return (
                    <div
                      key={size}
                      className="bg-white p-2.5 rounded-xl border border-slate-200/90 shadow-2xs space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-extrabold text-[11px] text-slate-900 font-mono">
                          {size}
                        </span>
                        {/* Tag category hint for uniform sizing */}
                        {parseInt(size) > 0 && (
                          <span className="text-[9px] font-bold text-slate-400">
                            {parseInt(size) <= 130 ? 'Kanak2' : parseInt(size) <= 170 ? 'Remaja' : 'Dewasa'}
                          </span>
                        )}
                      </div>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1.5 text-slate-400 font-extrabold text-[10px]">RM</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={currentPrice || ''}
                          onChange={e => handleUpdateSizePrice(size, e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-8 pr-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-extrabold text-slate-900 focus:outline-none focus:border-asmm-red"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasSizePricing && parsedSizes.length === 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 p-2.5 rounded-xl border border-amber-200 font-medium">
              Sila masukkan senarai pilihan saiz di atas terlebih dahulu untuk menetapkan harga setiap saiz.
            </p>
          )}
        </div>

        {/* SECTION 3: VARIASI PRODUK */}
        <div>
          <label className="block text-slate-700 font-extrabold mb-1">
            Pilihan Variasi Tambahan (Asingkan dengan koma)
          </label>
          <input
            type="text"
            value={formVariations}
            onChange={e => setFormVariations(e.target.value)}
            placeholder="Contoh: Hitam Sulaman Standard, Hitam Sulaman Khas Emas"
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-asmm-red shadow-xs"
          />
        </div>

        {/* SECTION: KAEDAH PENGHANTARAN */}
        <div className="space-y-3 border border-slate-200 rounded-2xl p-4 bg-white shadow-xs">
          <div>
            <label className="block text-slate-800 font-extrabold text-xs">
              Pilihan Kaedah Penghantaran
            </label>
            <p className="text-[10px] text-slate-400 font-medium">
              Tetapkan cara serahan pesanan barangan ini kepada pembeli
            </p>
          </div>

          <div className="pt-1">
            {/* Option: Ambil di Gelanggang */}
            <label className={`p-3 rounded-xl border flex items-start gap-3 cursor-pointer transition-all ${
              formDeliveryMethods.includes('Ambil di Gelanggang')
                ? 'border-asmm-red bg-red-50/20 ring-1 ring-asmm-red'
                : 'border-slate-200 bg-slate-50 opacity-60'
            }`}>
              <input
                type="checkbox"
                checked={formDeliveryMethods.includes('Ambil di Gelanggang')}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormDeliveryMethods(prev => [...prev.filter(m => m !== 'Ambil di Gelanggang'), 'Ambil di Gelanggang']);
                  } else {
                    showToast('info', 'Pilihan Wajib', 'Buat masa ini persatuan mewajibkan kaedah Ambil di Gelanggang (Pickup).');
                  }
                }}
                className="mt-0.5 rounded text-asmm-red focus:ring-asmm-red"
              />
              <div className="min-w-0">
                <span className="text-xs font-black text-slate-900 block">
                  Pickup
                </span>
                <span className="text-[10px] text-slate-500 font-medium block leading-tight mt-0.5">
                  Serahan fizikal di gelanggang rasmi persatuan semasa hari latihan.
                </span>
              </div>
            </label>
          </div>
        </div>

        {/* SECTION 4: GAMBAR PRODUK & CLOUDINARY UPLOAD */}
        <div className="space-y-2 border border-slate-200 rounded-2xl p-4 bg-white shadow-xs">
          <div className="flex items-center justify-between">
            <label className="block text-slate-700 font-extrabold">
              Gambar Produk
            </label>
            <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Cloudinary Vault (asmm/merchandise)
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 items-start sm:items-center">
            {/* Thumbnail */}
            <div className="w-12 h-12 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden shrink-0 flex items-center justify-center">
              {formPhotoUrl ? (
                <img
                  src={formPhotoUrl}
                  alt="Pratonton"
                  className="w-full h-full object-cover"
                  onError={e => { (e.target as HTMLImageElement).src = '/logo-pssasmm.png'; }}
                />
              ) : (
                <ImageIcon className="w-5 h-5 text-slate-300" />
              )}
            </div>

            <div className="flex-1 w-full flex gap-2">
              <input
                type="text"
                value={formPhotoUrl}
                onChange={e => setFormPhotoUrl(e.target.value)}
                placeholder="Contoh: https://res.cloudinary.com/... atau baju-silat.jpg"
                className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-mono text-slate-800 focus:outline-none focus:border-asmm-red shadow-xs"
              />

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProductImageUpload}
                className="hidden"
              />

              <button
                type="button"
                disabled={isUploadingPhoto}
                onClick={() => fileInputRef.current?.click()}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 cursor-pointer ${
                  isUploadingPhoto
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                    : 'bg-slate-900 hover:bg-slate-800 text-white shadow-xs'
                }`}
                title="Pilih gambar dari komputer dan muat naik terus ke Cloudinary"
              >
                <Upload className={`w-3.5 h-3.5 ${isUploadingPhoto ? 'animate-bounce' : ''}`} />
                <span>{isUploadingPhoto ? 'Memuat Naik...' : 'Muat Naik'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* SECTION 5: DESKRIPSI PRODUK */}
        <div>
          <label className="block text-slate-700 font-extrabold mb-1">Deskripsi Produk</label>
          <textarea
            rows={3}
            value={formDescription}
            onChange={e => setFormDescription(e.target.value)}
            placeholder="Terangkan bahan kain, keselesaan, ketahanan, kegunaan rasmi persatuan..."
            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-asmm-red shadow-xs"
          />
        </div>

        {/* SECTION 6: SPESIFIKASI TEKNIKAL (SHOPEE STYLE SPECS) */}
        <div className="space-y-2 border border-slate-200 rounded-2xl p-4 bg-white shadow-xs">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-slate-700 font-extrabold">Spesifikasi Teknikal</label>
              <p className="text-[10px] text-slate-400">Parameter butiran produk (cth: Bahan, Warna, Kandungan Pakej)</p>
            </div>
            <button
              type="button"
              onClick={addSpecField}
              className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-extrabold text-[10px] cursor-pointer"
            >
              + Tambah Spesifikasi
            </button>
          </div>

          <div className="space-y-2 bg-slate-50 p-3 rounded-xl border border-slate-150">
            {formSpecs.map((spec, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Tajuk (e.g. Bahan)"
                  value={spec.key}
                  onChange={e => updateSpecField(idx, 'key', e.target.value)}
                  className="w-1/3 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800"
                />
                <input
                  type="text"
                  placeholder="Nilai (e.g. Kapas Premium 100%)"
                  value={spec.value}
                  onChange={e => updateSpecField(idx, 'value', e.target.value)}
                  className="flex-1 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800"
                />
                {formSpecs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSpecField(idx)}
                    className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors cursor-pointer"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={isSaving || isUploadingPhoto}
            className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-extrabold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span>{isSaving ? 'Menyimpan...' : 'Simpan Butiran Produk'}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};

